<template>
  <div>
    <v-row justify="space-between">
      <v-col cols="auto">
        <SearchText
          :text="'Search by name'"
          :value="'text'"
          @searchTextChanged="searchTextChanged"
          @keypress.enter.native.prevent="triggerSearch"
          @triggerSearch="triggerSearch"
          @triggerClear="triggerClear"
        ></SearchText>
      </v-col>
      <v-col v-if="$store.getters.roles === 'super-admin'" class="d-flex align-center">
        <span class="mr-2 primary200--text font-weight-bold">Enabled Providers</span>
        <v-switch v-model="providersEnabled" color="success"></v-switch>
      </v-col>
      <v-col cols="auto" class="d-flex align-center">
        <v-btn
          v-if="$store.getters.roles == 'super-admin'"
          @click="downloadProviders"
          outlined
          class="black--text font-weight-bold mb-2"
          rounded
          large
        >
          <v-icon color="black" small class="mr-2">
            $vuetify.icons.plus
          </v-icon>
          Download
        </v-btn>
        <v-btn
          @click="triggerBulkEdit"
          outlined
          class="white--text font-weight-bold mb-2 black ml-2"
          rounded
          large
        >
          Bulk Edit
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="items"
      class="elevation-0"
      :options.sync="options"
      :server-items-length="totalItems"
      :loading="loading"
      item-class="properties"
      :dense="false"
      :footer-props="footerProps"
      :single-select="false"
      v-model="selected"
      show-select
    >
      <template v-slot:item.logo="{ item }">
        <div class="table-logo">
          <v-img
            class="my-2"
            style="border-radius: 8px;"
            lazy-src="@/assets/img/blur.jpg"
            height="65"
            width="65"
            :src="item.logoUrl"
            contain
          ></v-img>
        </div>
      </template>
      <template v-slot:item.name="{ item }">
        <div class="font-weight-bold">{{ item.name }}</div>
      </template>
      <template v-slot:item.hostingInstitution="{ item }">
        <div class="font-weight-bold">{{ item.hostingInstitution.name }}</div>
      </template>
      <template v-slot:item.adminContactName="{ item }">
        <div class="font-weight-bold">{{ item.adminContactName }}</div>
      </template>
      <template v-slot:item.status="{ item }">
        <v-chip v-if="item.disabled" outlined color="danger" class="font-weight-bold">
          <v-icon left>
            mdi-exclamation
          </v-icon>
          Disabled
        </v-chip>
        <v-chip v-else-if="item.draft" outlined color="orange" class="font-weight-bold">
          <v-icon left>
            mdi-circle
          </v-icon>
          Draft
        </v-chip>
        <v-chip v-else outlined color="success" class="font-weight-bold">
          <v-icon left>
            mdi-check
          </v-icon>
          Enabled
        </v-chip>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-layout>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
                @click="onEditItem(item)"
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-pencil-outline
                </v-icon>
              </v-btn>
            </template>
            {{ $t('edit') }}
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
                @click="onOverviewItem(item)"
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-eye
                </v-icon>
              </v-btn>
            </template>
            {{ $t('Overview') }}
          </v-tooltip>
          <v-tooltip bottom v-if="!item.disabled">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                @click="(itemToBeDisabled = item), (disabledDialog = true)"
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-cancel
                </v-icon>
              </v-btn>
            </template>
            {{ $t('disable.message') }}
          </v-tooltip>
          <v-tooltip bottom v-if="item.disabled && $store.getters.roles === 'super-admin'">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                @click="(itemToBeDeleted = item), (deleteDialog = true)"
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-delete
                </v-icon>
              </v-btn>
            </template>
            {{ $t('delete.message') }}
          </v-tooltip>
          <v-tooltip bottom v-if="item.disabled">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                @click="(itemToBeDisabled = item), (disabledDialog = true)"
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-check
                </v-icon>
              </v-btn>
            </template>
            {{ $t('enable.message') }}
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                @click="onEditMembers(item)"
                v-bind="attrs"
                v-on="on"
                x-small
                fab
                class="mr-2"
                color="white"
              >
                <v-icon style="color: rgba(0, 0, 0, 0.87)!important;">
                  mdi-account-multiple
                </v-icon>
              </v-btn>
            </template>
            {{ $t('members') }}
          </v-tooltip>
        </v-layout>
      </template>
    </v-data-table>

    <!-- Dialog for Delete HI -->
    <v-dialog v-if="itemToBeDeleted !== null" v-model="deleteDialog" max-width="1000px" persistent>
      <v-card class="py-4 px-5 px-sm-16 px-md-16 px-lg-16 px-xl-16 py-sm-8 py-md-8 py-lg-8 py-xl-8">
        <v-card-title class="px-0 py-0">
          <v-row class="mx-0 px-2" align="center">
            <v-col cols="11" class="justify-center d-flex">
              <img
                alt="Logo"
                width="180"
                class="rounded-0 text-center"
                :src="require('@/assets/svg/logo.svg')"
              />
            </v-col>
            <v-col cols="1" class="pl-0">
              <v-btn fab small text @click="deleteDialog = false">
                <v-icon color="neutral500">
                  mdi-close
                </v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-title>

        <v-divider class="custom-divider mt-10"></v-divider>

        <v-card-text class="justify-center d-flex mt-2">
          <v-icon size="150" color="red">$vuetify.icons.deleteCircle</v-icon>
        </v-card-text>
        <v-card-text class="text-center">
          <h3>Delete your Provider?</h3>
        </v-card-text>
        <v-card-text>
          <p class="text-center pl-16 pr-16">
            Are you sure you want to permanently delete your provider? <br />
            This action
            <b class="red--text">cannot be undone.</b>
          </p>
        </v-card-text>
        <v-card-actions>
          <v-row style="width: 100%">
            <v-col cols="6">
              <v-btn
                @click="deleteDialog = false"
                block
                depressed
                rounded
                large
                class="neutral800--text font-weight-bold"
                color="neutral"
                outlined
                >{{ $t('cancel') }}
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                @click="deleteProvider"
                block
                depressed
                rounded
                large
                class="white--text font-weight-bold"
                color="red"
                >{{ $t('confirm') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog Disable/Enable -->
    <v-dialog v-if="itemToBeDisabled !== null" v-model="disabledDialog" max-width="500">
      <v-card class="py-2 px-5 px-sm-16 px-md-16 px-lg-16 px-xl-16 py-sm-8 py-md-8 py-lg-8 py-xl-8">
        <v-card-title>{{
          itemToBeDisabled.disabled ? $t('enable.message') : $t('disable.message')
        }}</v-card-title>
        <v-card-text>
          {{ itemToBeDisabled.disabled ? $t('enable.confirm') : $t('disable.confirm') }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            depressed
            rounded
            large
            class="neutral800--text font-weight-bold"
            color="neutral"
            outlined
            @click="disabledDialog = false"
            >{{ $t('cancel') }}
          </v-btn>
          <v-btn
            v-if="itemToBeDisabled.disabled"
            depressed
            rounded
            large
            class="white--text font-weight-bold"
            color="danger"
            @click="onEnableItem"
            :disabled="disableDisabled"
            >{{ $t('enable.message') }}
          </v-btn>
          <v-btn
            v-else
            depressed
            rounded
            large
            class="white--text font-weight-bold"
            color="danger"
            @click="onDisabledItem"
            :disabled="disableDisabled"
            >{{ $t('disable.message') }}
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bulk Edit Dialog -->
    <v-dialog v-model="dialogBulkEdit" max-width="500" persistent>
      <v-card>
        <v-card-title class="pb-5">
          <v-row>
            <v-col cols="12" class="justify-start d-flex">
              Bulk Edit
            </v-col>
          </v-row>
          <v-btn icon @click="dialogBulkEdit = false" left>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="mb-0 pb-0">
          <label>Provider to copy from</label>
          <p class="label-info">
            Select the Provider you want to use as protorype
          </p>
          <input-autocomplete-search
            v-model="bulkEdit.providerPrototype"
            :item-value="'id'"
            :item-text="'name'"
            :url="'/api/v1/providers?name='"
            label="Provider to copy from"
            prepend-inner-icon="mdi-magnify"
            clearable
            return-object
            outlined
          ></input-autocomplete-search>
        </v-card-text>
        <v-card-text>
          <v-checkbox v-model="bulkEdit.logo" label="Same Logo" class="mt-0 pt-0" />
        </v-card-text>
        <v-card-text>
          <v-checkbox
            v-model="bulkEdit.publicContact"
            label="Same Public Contact"
            class="mt-0 pt-0"
          />
        </v-card-text>
        <v-card-actions class="pb-5">
          <v-row>
            <v-col cols="6">
              <v-btn
                @click="cancelBulkEdit"
                block
                large
                class="rounded-lg black--text  font-weight-bold"
                outlined
                color="black"
                >{{ $t('cancel') }}
              </v-btn>
            </v-col>
            <v-col cols="6">
              <v-btn
                @click="submitBulkEdit"
                large
                class="rounded-lg white--text  font-weight-bold"
                color="neutral"
                block
                >{{ $t('submit') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import EventBus from '@/helpers/event-bus.js';
import util from '@/mixins/util.js';
import SearchText from '@/components/shared/search/SearchText';
import InputAutocompleteSearch from '@/components/shared/inputs/InputAutocompleteSearch.vue';

export default {
  name: 'TableProviders',
  components: { SearchText, InputAutocompleteSearch },
  mixins: [util],
  props: {
    urlApi: String,
    search: Object,
    searchAttributes: Array,
    viewRouter: String,
  },
  data: () => ({
    providersEnabled: true,
    firstLoad: true,
    totalItems: 0,
    items: [],
    loading: true,
    options: {},
    itemToBeDisabled: null,
    disabledDialog: false,
    disableDisabled: false,
    dialogBulkEdit: false,
    deleteDialog: false,
    itemToBeDeleted: null,
    selected: [],
    bulkEdit: {
      providers: [],
      providerPrototype: null,
      logo: false,
      publicContact: false,
    },
    headers: [
      { text: '', value: 'actions', sortable: false },
      {
        text: 'Logo',
        value: 'logo',
        width: '10px',
        sortable: false,
      },
      { text: 'Name', value: 'name', sortable: true },
      { text: 'Institution', value: 'hostingInstitution', sortable: true },
      { text: 'Administrator', value: 'adminContactName', sortable: false },
      { text: 'Status', value: 'status', sortable: false },
    ],
    footerProps: { 'items-per-page-options': [10, 15, 25, 50, 100] },
  }),
  watch: {
    options: {
      handler() {
        console.log('[Table] Options watch');
        if (!this.firstLoad) {
          this.changeOptions();
        }
      },
      deep: true,
    },
    providersEnabled() {
      this.changeOptions();
    },
  },
  mounted() {
    console.log('[Table] Mounted');
    if (this.firstLoad) {
      /* Populate table from route */
      this.options.page = this.$route.query.index ? Number(this.$route.query.index) + 1 : 1;
      this.options.itemsPerPage = this.$route.query.size ? Number(this.$route.query.size) : 10;
      this.options.sortBy = [];
      this.options.sortDesc = [];
      if (this.$route.query.providersEnabled !== undefined) {
        this.providersEnabled = this.$route.query.providersEnabled === 'false' ? false : true;
      }
      if (this.$route.query.providersEnabled == false) {
        this.providersEnabled = false;
      }
      if (this.$route.query.sort) {
        const sortList = this.$route.query.sort.split(',');
        for (let i = 0; i < sortList.length; i += 1) {
          this.options.sortBy.push(sortList[i].split(' ')[0]);
          if (sortList[i].split(' ')[1] === 'asc') {
            this.options.sortDesc.push(false);
          } else {
            this.options.sortDesc.push(true);
          }
        }
      }
      for (let i = 0; i < this.searchAttributes.length; i += 1) {
        const attr = this.searchAttributes[i];
        if (this.$route.query[attr]) {
          this.$set(this.search, attr, this.$route.query[attr]);
        }
      }
    }
    this.getDataFromApi().then(data => {
      this.items = data.items;
      this.totalItems = data.total;
      this.firstLoad = false;
    });
  },
  methods: {
    searchTextChanged(obj) {
      console.log('[Table] searchTextChanged');
      this.search[obj.value] = obj.search;
      console.log(this.search);
    },
    triggerSearch() {
      console.log('[Table] Search watch');
      this.options.page = 1;
      this.getDataFromApi().then(data => {
        this.items = data.items;
        this.totalItems = data.total;
      });
    },
    triggerClear() {
      console.log('[Table] Clear watch');

      this.search = {};

      this.options.page = 1;
      this.getDataFromApi().then(data => {
        this.items = data.items;
        this.totalItems = data.total;
      });
    },
    changeOptions() {
      this.$router.push({
        path: this.$router.path,
        query: this.getParams(),
      });
      let currentPathName = this.$router.currentRoute.name;
      localStorage[currentPathName] = this.$router.currentRoute.fullPath;
    },
    getDataFromApi() {
      console.log('[Table] Loading data');
      this.loading = true;
      EventBus.$emit('waiting', true);
      return new Promise(resolve => {
        this.axios
          .get(`${this.urlApi}?`, {
            params: this.getParams(),
          })
          .then(res => {
            let items = [];
            let total = 0;
            if (res.status === 200) {
              items = res.data.content;
              total = res.data.total;
            }
            setTimeout(() => {
              this.loading = false;
              resolve({
                items,
                total,
              });
            });
          })
          .finally(() => {
            EventBus.$emit('waiting', false);
          });
      });
    },
    getParams() {
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      const query = {
        index: page - 1,
        size: itemsPerPage,
        providersEnabled: this.providersEnabled,
      };
      let sort = '';
      for (let i = 0; i < sortBy.length; i += 1) {
        if (i !== 0) {
          sort += ',';
        }
        let dir = 'asc';
        if (sortDesc[i]) {
          dir = 'desc';
        }
        sort += `${sortBy[i]} ${dir}`;
      }
      if (sort !== '') {
        query.sort = sort;
      }
      for (let i = 0; i < this.searchAttributes.length; i += 1) {
        const attr = this.searchAttributes[i];
        if (this.search[attr]) {
          query[attr] = this.search[attr];
        }
      }
      return query;
    },
    onViewItem(value) {
      console.log('Click View ' + value.id);
      this.$router.push({
        name: this.viewRouter,
        query: { id: value.id },
      });
    },
    onDisabledItem() {
      this.disableDisabled = true;
      let value = this.itemToBeDisabled;
      console.log('Click Disabled ' + value.id);
      EventBus.$emit('waiting', true);
      this.axios
        .post('/api/v1/providers/disable/' + value.id)
        .then(() => {
          console.log('Success');
          EventBus.$emit('snackbar', {
            text: this.$i18n.t('disable.success'),
          });
          this.disabledDialog = false;
          this.getDataFromApi().then(data => {
            this.items = data.items;
            this.totalItems = data.total;
          });
          this.disableDisabled = false;
        })
        .catch(error => {
          console.log(error);
          this.disableDisabled = false;
        })
        .finally(EventBus.$emit('waiting', false));
    },
    onEnableItem() {
      this.disableDisabled = true;
      let value = this.itemToBeDisabled;
      console.log('Click Disabled ' + value.id);
      EventBus.$emit('waiting', true);
      this.axios
        .post('/api/v1/providers/enable/' + value.id)
        .then(() => {
          console.log('Success');
          EventBus.$emit('snackbar', {
            text: this.$i18n.t('enable.success'),
          });
          this.disabledDialog = false;
          this.getDataFromApi().then(data => {
            this.items = data.items;
            this.totalItems = data.total;
          });
          this.disableDisabled = false;
        })
        .catch(error => {
          console.log(error);
          this.disableDisabled = false;
        })
        .finally(EventBus.$emit('waiting', false));
    },
    onEditItem(value) {
      console.log('Click Edit ' + value.id);
      this.$router.push({
        name: 'EditProvider',
        params: { id: value.id },
        query: {
          fromTable: true,
          ...this.getParams(),
        },
      });
    },
    onOverviewItem(value) {
      console.log('Click Overview ' + value.id);
      this.$router.push({
        name: 'OverviewProvider',
        params: { id: value.id },
        query: {
          fromTable: true,
          ...this.getParams(),
        },
      });
    },
    onEditMembers(value) {
      console.log('Click Members for provider ' + value.id);
      this.$router.push({
        name: 'EditAdminProviderMembers',
        params: { id: value.id },
        query: {
          provider: true,
          fromTable: true,
          ...this.getParams(),
        },
      });
    },
    downloadProviders() {
      this.axios
        .get('api/v1/providers/admin/download', {
          responseType: 'blob',
        })
        .then(response => {
          const contentDisposition = response.headers['content-disposition'];
          let fileName = 'providers.xlsx';
          if (contentDisposition) {
            const fileNameMatch = contentDisposition.match(/filename="([^"]*)"/);
            if (fileNameMatch) {
              fileName = fileNameMatch[1];
            }
          }

          const url = window.URL.createObjectURL(new Blob([response.data]));

          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', fileName);

          document.body.appendChild(link);
          link.click();
          link.remove();
        })
        .catch(error => {
          console.error('Error downloading file:', error);
        });
    },
    triggerBulkEdit() {
      this.dialogBulkEdit = true;
    },
    cancelBulkEdit() {
      this.bulkEdit.providerPrototype = null;
      this.bulkEdit.logo = false;
      this.bulkEdit.publicContact = false;
      this.dialogBulkEdit = false;
    },
    submitBulkEdit() {
      EventBus.$emit('waiting', true);
      this.bulkEdit.providers = this.selected;
      this.axios
        .put('/api/v1/provider-settings/bulk', this.bulkEdit)
        .then(() => {
          console.log('Success');
          EventBus.$emit('snackbar', {
            text: this.$i18n.t('Providers Updates'),
          });
          this.dialogBulkEdit = false;
          this.getDataFromApi().then(data => {
            this.items = data.items;
            this.totalItems = data.total;
          });
        })
        .catch(error => {
          console.log({ error });
        })
        .finally(() => {
          EventBus.$emit('waiting', false);
        });
    },
    deleteProvider() {
      let value = this.itemToBeDeleted;
      console.log('Click Delete' + value.id);
      EventBus.$emit('waiting', true);
      this.axios
        .delete('api/v1/providers/provider-delete/' + value.id)
        .then(() => {
          console.log('Success');
          EventBus.$emit('snackbar', {
            text: this.$i18n.t('delete.success'),
          });
          this.deleteDialog = false;
          this.getDataFromApi().then(data => {
            this.items = data.items;
            this.totalItems = data.total;
          });
        })
        .catch(error => {
          console.log(error);
          this.deleteDialog = false;

          const message = error?.response?.data?.description;

          EventBus.$emit('snackbar', {
            text: message,
            color: 'warning',
          });
        })
        .finally(EventBus.$emit('waiting', false));
    },
  },
};
</script>

<style>
.v-application--is-ltr .v-data-table__mobile-row__cell {
  text-align: left;
}

tr {
  min-height: 200px;
  height: 100%;
}

#table-providers
  .theme--light.v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr
  > td:first-child {
  width: 97px !important;
}
</style>
